<template>
  <div>
    <div class="flex xs:hidden">
      <h3>History</h3>
    </div>
    <a-spin :spinning="loading" size="large">
      <template slot="indicator">
        <div>
          <lottie :options="loadingAnimationOptions" />
        </div>
      </template>
      <div v-for="(time, index) in events" :key="index">
        <div v-for="(day, key) in time" :key="key">
          <h5 class="mt-8 mb-4">{{ day.label }}</h5>
          <a-collapse
            :bordered="false"
            :accordion="false"
            :activeKey="currentRecordingId"
            @change="setActivePanel"
          >
            <a-collapse-panel
              :showArrow="false"
              v-for="event in day.events"
              v-bind:key="event.recordedAt"
              forceRender
            >
              <template slot="header">
                <div class="header">
                  <p class="time sm:w-3/24 xs:mr-1">
                    {{ eventTime(event.recordedAt) }}
                  </p>
                  <div class="title sm:w-10/12 xs:w-10/12">
                    <div v-if="medicationUsedEvent(event)" class="info">
                      <div class="circle" :class="medicationClass(event)" />
                      <p>{{ medicationTitle(event) | capitalize }}</p>
                    </div>
                    <template v-else-if="recordingEvent(event)" class="info">
                      <div
                        v-if="recordingPercentage(event.recording) === 0"
                        class="zero-percentage-bar"
                      />
                      <a-progress
                        v-else
                        :percent="recordingProgress(recordingPercentage(event.recording))"
                        :strokeWidth="32"
                        :showInfo="false"
                        class="px-2"
                      />
                      <h3 class="mb-0 px-2">{{ recordingPercentage(event.recording) }}%</h3>
                      <AudioPlayback
                        v-if="event.recording"
                        :url="event.recording.recordingFile.url"
                        :id="event.recordedAt"
                        @change="setActivePanel(event.recordedAt)"
                      />
                    </template>
                    <div v-else-if="selfAssessmentEvent(event)" class="info">
                      <img
                        class="breathing-image"
                        :src="breathingImage(event.selfAssessment.breathingDifficulty)"
                        alt="breathing"
                      />
                      <p>
                        {{ breathingText(event.selfAssessment.breathingDifficulty) }}
                      </p>
                    </div>
                  </div>
                  <div class="xs:hidden">
                    <a-icon v-if="currentRecordingId == event.recordedAt" type="up" class="up" />
                    <a-icon v-else type="down" class="down" />
                  </div>
                </div>
              </template>
              <div class="flex">
                <div class="sm:w-3/24" />
                <div class="sm:w-9/12 xs:w-full">
                  <div class="bordered-devider" />
                  <MedicationDisplay
                    v-if="medicationUsedEvent(event)"
                    :medicationUse="event.medicationUse"
                  />
                  <div
                    v-else-if="recordingEvent(event)"
                    class="recording-body sm:flex-row xs:flex-col"
                  >
                    <SymptomsTriggersDisplay
                      :triggers="event.recording.triggers"
                      :symptoms="event.recording.symptoms"
                    />
                    <AudioPlaybackSlider :id="event.recordedAt" class="sm:w-1/3 xs:w-full" />
                  </div>
                  <template v-else-if="selfAssessmentEvent(event)">
                    <SymptomsTriggersDisplay
                      :triggers="event.selfAssessment.triggers"
                      :symptoms="event.selfAssessment.symptoms"
                    />
                    <p class="mb-2">
                      <b>Notes: &nbsp;</b>
                      {{ selfAssessmentNotes(event.selfAssessment) | capitalize }}
                    </p>
                  </template>
                </div>
                <div class="sm:w-3/24" />
              </div>
            </a-collapse-panel>
          </a-collapse>
        </div>
      </div>
    </a-spin>
  </div>
</template>
<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { FETCH_HISTORY, GROUPED_EVENTS, CURRENT_RECORDING, SET_CURRENT_RECORDING } from '@/store';
import AudioPlayback from '@/components/AudioPlayback.vue';
import AudioPlaybackSlider from '@/components/AudioPlaybackSlider.vue';
import MedicationDisplay from '@/components/MedicationDisplay.vue';
import SymptomsTriggersDisplay from '@/components/SymptomsTriggersDisplay.vue';
import { EventType, MedicationCategory, BreathingDifficulty } from '@/data-service/types';
import { prettifyString } from '@/utils';

// eslint-disable-next-line
import Lottie from "vue-lottie";
import * as loaderColor from '@/assets/loader-color.json';

@Component({
  components: {
    AudioPlayback,
    AudioPlaybackSlider,
    SymptomsTriggersDisplay,
    MedicationDisplay,
    lottie: Lottie
  }
})
export default class RecordingHistory extends Vue {
  loading = false;
  loadingAnimationOptions = {
    animationData: loaderColor.default
  };

  mounted() {
    this.fetchRecordingHistory();
  }

  get events() {
    return this.$store.getters[GROUPED_EVENTS];
  }

  get currentRecordingId() {
    return this.$store.getters[CURRENT_RECORDING];
  }

  setActivePanel(panel) {
    this.$store.commit(SET_CURRENT_RECORDING, panel);
  }

  eventTime(recordedAt) {
    return new Date(recordedAt).toLocaleString([], {
      hour12: true,
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  medicationUsedEvent(event) {
    return EventType[event.type] === EventType.MEDICATION_USE;
  }

  recordingEvent(event) {
    return EventType[event.type] === EventType.RECORDING;
  }

  selfAssessmentEvent(event) {
    return EventType[event.type] === EventType.SELF_ASSESSMENT;
  }

  medicationClass(event) {
    return MedicationCategory[event.medicationUse.medication.category] ===
      MedicationCategory.PREVENTER
      ? 'red'
      : 'blue';
  }

  medicationTitle(event) {
    return `${event.medicationUse.medication.category.toLowerCase()} used`;
  }

  recordingPercentage(recording) {
    return Math.round(recording.wheezeRate);
  }

  recordingProgress(percent) {
    switch (percent) {
      case 0:
        return 0;
      case 1:
        return parseInt((Math.log(2) / Math.log(50) / 2) * 100);
      default:
        return parseInt((Math.log(percent) / Math.log(50)) * 100);
    }
  }

  breathingText(breathingDifficulty) {
    if (BreathingDifficulty[breathingDifficulty]) {
      return `Breathing: ${prettifyString(BreathingDifficulty[breathingDifficulty])}`;
    }
    return '';
  }

  breathingImage(breathingDifficulty) {
    if (BreathingDifficulty[breathingDifficulty]) {
      const text = BreathingDifficulty[breathingDifficulty].toLowerCase();
      return require(`@/assets/breathing-${text}.svg`);
    }
    return require(`@/assets/help-circle.svg`);
  }

  selfAssessmentNotes(selfAssessment) {
    return selfAssessment.notes ? selfAssessment.notes : '-';
  }

  fetchRecordingHistory() {
    this.loading = true;
    this.$store
      .dispatch(FETCH_HISTORY)
      .catch(err => {
        this.$message.error('Failed to retrive recording history');
      })
      .finally(() => (this.loading = false));
  }
}
</script>
<style lang="postcss" scoped>
.header {
  @apply flex items-center justify-between;
  & .time {
    @apply mb-0 px-2 text-base;
    color: #878f8f;
  }
  & .title {
    @apply flex items-center justify-start;
  }
}

.up {
  @apply px-2 text-lg;
  color: #1976d2;
}

.down {
  @apply px-2 text-lg;
  color: #878f8f;
}

.info {
  @apply h-8 flex items-center;
  & p {
    @apply mb-0 pl-6;
  }
  & .circle {
    display: inline-block;
    min-width: 2rem;
    height: 2rem;
    line-height: 1.5rem;
    border-radius: 50%;
  }
  & .red {
    background-color: #f97072;
    border-color: #f97072;
  }
  & .blue {
    background-color: #22a9df;
    border-color: #22a9df;
  }
  & .breathing-image {
    width: 2rem;
    height: 2rem;
  }
}

.recording-body {
  @apply flex justify-between;
}

.zero-percentage-bar {
  @apply mx-2 h-8 w-full;
  border-left: 2px solid #cfd7d7;
}

.bordered-devider {
  @apply mb-4;
  border-top: 1px solid #edf1ed;
}
</style>
